import { useEffect, useMemo, useRef, useState } from 'react'
import { SubmitHandler } from 'react-hook-form'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { useQueryClient } from '@tanstack/react-query'
import { t } from 'i18next'
import {
  AccountContactTranslation,
  AccountContactTypes,
  PersonDefaultOptions,
} from 'components/accountContact/helpers/constants'
import { AccountContactData, PersonSelectorData } from 'components/accountContact/helpers/types'
import { useAccountPreferenceMutation } from 'hooks/mutations/useAccountPreferenceMutation'
import { useInviteTeamMemberMutation } from 'hooks/mutations/useInviteTeamMemberMutation'
import { useAccountPreferencesQuery } from 'hooks/queries/useAccountPreferencesQuery'
import { useAccountUserGroupsQuery } from 'hooks/queries/useAccountUserGroupsQuery'
import { useProfileQuery } from 'hooks/queries/useProfileQuery'
import { SelectedSignatory } from 'types/accounts'
import { useAccountUsersQuery } from './useAccountUsersQuery'
import { useDeleteTeamMemberMutation } from './useDeleteTeamMemberMutation'

export const useSignatory = (
  personSelectorData: PersonSelectorData,
  accountContactsList: AccountContactData[],
  onContinue: () => void
) => {
  const queryClient = useQueryClient()
  const cachedProfile = queryClient.getQueryData(reactQueryKeys.profile())
  const { data: profile } = useProfileQuery(!cachedProfile)
  const { data: accountUserGroups } = useAccountUserGroupsQuery()
  const { data: preferencesData, refetch: refetchPreferences } = useAccountPreferencesQuery()

  const { mutate: postInviteTeamMember, isPending: isLoadingSubmitRequest } =
    useInviteTeamMemberMutation()
  const { mutate: updateSignatoryPreference, isPending: isUpdatingSignatoryPreference } =
    useAccountPreferenceMutation<SelectedSignatory>()
  const { data: accountUsers, isLoading: isLoadingAccountUsers } = useAccountUsersQuery()
  const { mutate: deleteTeamMember, isPending: isDeletingTeamMember } =
    useDeleteTeamMemberMutation()

  const [selectedSignatory, setSelectedSignatory] = useState<SelectedSignatory | undefined>(
    preferencesData?.selectedSignatory || undefined
  )
  const [selectedReference, setSelectedReference] = useState<string | undefined>(undefined)
  const [emailSent, setEmailSent] = useState<boolean>(false)
  const [submittedEmail, setSubmittedEmail] = useState<string>('')
  const [apiError, setApiError] = useState<string | null>(null)

  const workspaceOwnerGroup = accountUserGroups?.find(group => group.name === 'Workspace Owner')
  const preferencesDataRef = useRef(preferencesData)

  useEffect(() => {
    if (preferencesData && preferencesData !== preferencesDataRef.current) {
      preferencesDataRef.current = preferencesData
      setSelectedSignatory(preferencesData?.selectedSignatory || undefined)
      setSelectedReference(preferencesData?.selectedSignatory?.reference || undefined)
    }
  }, [preferencesData])

  useEffect(() => {
    if (selectedSignatory && selectedSignatory.reference) {
      if (selectedSignatory.isMyself) {
        setSelectedReference(PersonDefaultOptions.MYSELF)
      } else {
        setSelectedReference(selectedSignatory?.reference)
      }
    }
  }, [selectedSignatory])

  const onSubmit: SubmitHandler<{ emailAddress: string }> = formValues => {
    setApiError(null)
    if (selectedSignatory?.reference) {
      postInviteTeamMember(
        {
          emailAddress: formValues.emailAddress,
          firstName: selectedSignatory?.firstName,
          lastName: selectedSignatory?.lastName,
          groups: [{ id: workspaceOwnerGroup?.id, name: workspaceOwnerGroup?.name }],
        },
        {
          onSuccess: () => {
            setEmailSent(true)
            setSubmittedEmail(formValues.emailAddress)
            queryClient.invalidateQueries({ queryKey: ['accountUsers'] })
            updateSignatoryPreference(
              {
                preferenceKey: 'selectedSignatory',
                value: { ...selectedSignatory, emailAddress: formValues.emailAddress },
              },
              {
                onSuccess: () => {
                  refetchPreferences()
                  if (profile?.emailAddress === selectedSignatory?.emailAddress) {
                    onContinue()
                  }
                },
                onError: error =>
                  setApiError(
                    error?.message || t('verification.appointSignatory.failedToUpdateSignatory')
                  ),
              }
            )
          },
          onError: error => {
            setApiError(
              error?.message || t('verification.appointSignatory.failedToInviteSignatory')
            )
          },
        }
      )
    }
  }

  const mergedContacts = useMemo(() => {
    const mergedMap = new Map<string, AccountContactData>()

    accountContactsList.forEach(contact => {
      const personRef = contact?.person?.reference

      if (mergedMap.has(personRef)) {
        const existingContact = mergedMap.get(personRef)!
        if (
          contact?.type === AccountContactTypes.ACCOUNT_REPRESENTATIVE &&
          contact?.contactInfo?.emailAddress
        ) {
          mergedMap.set(personRef, { ...existingContact, contactInfo: contact?.contactInfo })
        }
      } else {
        mergedMap.set(personRef, contact)
      }
    })

    return Array.from(mergedMap.values())
  }, [accountContactsList])

  const handlePersonChange = (updatedReference: string) => {
    setSelectedReference(updatedReference)
    if (updatedReference === PersonDefaultOptions.MYSELF) {
      const myselfAccountContact = mergedContacts?.find(
        person => person?.contactInfo?.emailAddress === profile?.emailAddress
      )
      if (myselfAccountContact) {
        setSelectedSignatory({
          reference: myselfAccountContact?.reference,
          emailAddress: myselfAccountContact?.contactInfo?.emailAddress,
          firstName: myselfAccountContact?.person?.firstName,
          lastName: myselfAccountContact?.person?.lastName,
          position:
            t(`accountContact.type.${AccountContactTranslation?.[myselfAccountContact?.type]}`) ||
            '',
          isMyself: true,
        })
      } else {
        setSelectedSignatory({
          reference: profile?.emailAddress,
          emailAddress: profile?.emailAddress,
          firstName: profile?.firstName,
          lastName: profile?.lastName,
          position: '',
          isMyself: true,
        })
      }
    } else {
      const foundPerson = personSelectorData?.personsData?.find(
        person => person?.reference === updatedReference
      )
      if (foundPerson) {
        const foundContact = mergedContacts?.find(
          contact => contact.person.reference === foundPerson.person.reference
        )
        if (foundContact) {
          setSelectedSignatory({
            reference: foundPerson?.reference,
            emailAddress: foundContact?.contactInfo?.emailAddress || undefined,
            firstName: foundPerson?.person?.firstName,
            lastName: foundPerson?.person?.lastName,
            position:
              t(`accountContact.type.${AccountContactTranslation?.[foundContact?.type]}`) || '',
            isMyself: false,
          })
        } else {
          setSelectedSignatory({
            reference: foundPerson?.reference,
            emailAddress: undefined,
            firstName: foundPerson?.person?.firstName,
            lastName: foundPerson?.person?.lastName,
            position: '',
            isMyself: false,
          })
        }
      }
    }
  }

  const removeSignatory = () => {
    if (selectedSignatory?.reference) {
      updateSignatoryPreference(
        { preferenceKey: 'selectedSignatory', value: {} as SelectedSignatory },
        {
          onSuccess: () => {
            // Signatory removed successfully
            refetchPreferences()
            const userToDelete = accountUsers?.find(
              user => user.emailAddress === selectedSignatory.emailAddress
            )
            if (userToDelete?.id) {
              deleteTeamMember(userToDelete.id)
            }
          },
        }
      )
    }
  }

  return {
    profile,
    preferencesData,
    selectedSignatory,
    selectedReference,
    emailSent,
    submittedEmail,
    apiError,
    setApiError,
    isLoadingSubmitRequest,
    isUpdatingSignatoryPreference,
    onSubmit,
    handlePersonChange,
    mergedContacts,
    removeSignatory,
    isDeletingTeamMember,
    isLoadingAccountUsers,
  }
}
